// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { bool, func, string, object, number, arrayOf } from 'prop-types';
import WarningIcon from '@material-ui/icons/Warning';
import { find, get } from 'lodash';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  FormLabel,
  Grid,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { KeyboardArrowDown, Lock, ReportProblem } from '@material-ui/icons';
import { sentenceCase } from 'change-case';
import copy from 'copy-to-clipboard';
import moment from 'moment';

import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import { FormTextField, Loading, LoadingDialog } from '../../../../../components';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  API_KEY_STATUS_DELETE_FAILED,
  API_KEY_STATUS_DISABLED,
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
  API_KEY_STATUS_PENDING_REGISTRATION,
  APPLICATION_STATUS_INCOMPLETE,
  AUTH_PROVIDER_TYPE_PORTAL_DCR,
  RESET,
  SUCCESS,
  FAIL,
} from '../../../../../constants';
import SimpleTable from '../../../../../components/Table/simpleTable';
import { getCertificateExpiryStatus } from '../../Certificates/utils';
import { lowerCase } from 'lodash';
import { hasCRUDPermissions } from '../../../../../utils';
import { API_KEYS_APPLICATION } from '../../../../../constants';
import RegisterClientInfoDialog from '../RegisterClientInfoDialog';

const displayItem = (applicationDetails, classes, item) => {
  const value = get(applicationDetails, item.key) || '';
  return (
    <div key={item.id} id={item.id} className={classes.oauthPanelItemContainer}>
      <Typography className={classes.oauthPanelTitle}>{item.label}</Typography>
         <Typography className={classes.oauthPanelDesc}>
          {item.type === 'link'
          ? value && value.split(",").map(url=> <li key={url}><Link href={url}>{url}</Link></li>)
          :item.useSentenceCase ? sentenceCase(value) : value
          }
        </Typography>
    </div>
  );
};

const editKeyDisabledStates = [
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
];

const registerKeyDisabledStates = [
  ...editKeyDisabledStates,
  APPLICATION_STATUS_INCOMPLETE,
];

const showCopied = (value, valueType, notifyMessages) => {
  let strType = '';
  if (valueType === 'API_KEY') {
    strType = 'API Key';
  } else if (valueType === 'SECRET') {
    strType = 'Secret';
  }
  notifyMessages(`${strType}: ${value} was copied to the clipboard.`, ALERT_SUCCESS, true);
};

const items = [
  {
    id: 'app-oauth-callback-url',
    key: 'oauthCallbackUrl',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.urls'),
    type: 'link',
  },
  {
    id: 'app-oauth-scope',
    key: 'oauthScope',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.scope'),
  },
  {
    id: 'app-oauth-type',
    key: 'oauthType',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.type'),
    useSentenceCase: true,
  },
];

const SingleKeyView = ({
  applicationDetails,
  authProviders,
  keyDetails,
  classes,
  notifyMessages,
  setIsEditKeyOpen,
  isLocking,
  isApiKeyExpiryEnabled,
  locked,
  disabledByInternal,
  appSecretHashingMetadata,
  regeneratedSecretDetails,
  regerateSecretStatus,
  registeredKeyDetails,
  registerKeyStatus,
  registerKeyError,
  deleteKey,
  deleteOAuthClient,
  regenerateSecret,
  registerKey,
  applicationUuid,
  defaultHashType,
  isEditDisabled,
  isPortalAdmin,
  isAdmin,
  onSaveDone,
  onRegistrationDialogClose,
  certificateList,
  startReportingDays,
  urgentReportingDays,
  idx,
  user,
  canAddKey,
 }) => {

    const intl = getI18n(useIntl());
    const [anchorEl, setAnchorEl] = useState(null);
    const [isSecretInfoDialogOpen, setIsSecretInfoDialogOpen] = useState(false);
    const [keyClientSecret, setKeyClientSecret] = useState('');
    const [registeredClientId, setRegisteredClientId] = useState('');
    const [clientMetadata, setClientMetadata] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [generatingSecret, setGeneratingSecret] = useState(false);
    const canEditOrDeleteKey = hasCRUDPermissions(user, ['UPDATE'], API_KEYS_APPLICATION)
      || hasCRUDPermissions(user, ['DELETE'], API_KEYS_APPLICATION);

    const [registeringKey, setRegisteringKey] = useState(false);
    const [isRegisteredKeyInfoDialogOpen, setIsRegisteredKeyInfoDialogOpen] = useState(false);
    const [showGenericLoadingDialog, setShowGenericLoadingDialog] = useState(false);

    const handleGenerateSecretByType = (type) => {
      generateSecret(type);
      setAnchorEl(false);
    }

    const displayHashingOptions = get(appSecretHashingMetadata, 'plaintextAllowed');

    const handleGenerateSecretClick = (event) => {
      if(displayHashingOptions) {
        setAnchorEl(event.currentTarget);
      } else {
        generateSecret(defaultHashType);
      }
    };

    const handleMenuClose = () => {
      setAnchorEl(false);
    };
    const deleteKeyOrAuthClient = () => {
      if (get(keyDetails, 'clientRegistrationType') === AUTH_PROVIDER_TYPE_PORTAL_DCR) {
        deleteOAuthClient({
          oauthClientDetails: {
            appUuid: applicationUuid,
            clientId: apiKey,
          },
          ignoreKeyStoreCheck: isPortalAdmin,
          isForceDelete: isPortalAdmin,
        });
      } else {
        deleteKey({
          keyDetails: {
            appUuid: applicationUuid,
            apiKey,
          },
          isForceDelete: isPortalAdmin,
          ignoreKeyStoreCheck: isPortalAdmin,
        });
      }
    };
    const getCertListColumns = () => [
      {
        id: 'name',
        label: intl.getI18nMessage('label.application.details.certificates.column.certificate'),
        value: ({ name }) => name,
      },{
        id: 'daysToExpiry',
        label: intl.getI18nMessage('label.application.details.certificates.column.days.to.expiry'),
        minwidth: 100,
        value: ({ expiryTs, uuid }) =>
          getCertificateExpiryStatus(
            expiryTs, uuid, classes, startReportingDays, urgentReportingDays),
      },
    ];
    useEffect(() => {
      if (generatingSecret) {
        if(regerateSecretStatus === 'SUCCESS') {
          const keySecret = get(regeneratedSecretDetails, 'keySecret');
          setKeyClientSecret(keySecret);
          setIsSecretInfoDialogOpen(true);
          setGeneratingSecret(false);
        } else if (regerateSecretStatus === 'FAIL') {
          notifyMessages(intl.getI18nMessage('label.application.details.keys.secret.update.error'), ALERT_ERROR);
          setGeneratingSecret(false);
        }
        setShowLoading(false);
      }
    },[regerateSecretStatus]);

    useEffect(() => {
      setShowGenericLoadingDialog(registerKeyStatus === RESET);
      if (registeringKey) {
        if(registerKeyStatus === SUCCESS) {
          const keySecret = get(registeredKeyDetails, 'clientSecret');
          const regClientId = get(registeredKeyDetails, 'clientId');
          const metadata = get(registeredKeyDetails, 'clientMetadata');
          setClientMetadata(metadata);
          setRegisteredClientId(regClientId);
          setKeyClientSecret(keySecret);
          setIsRegisteredKeyInfoDialogOpen(true);
          setRegisteringKey(false);
        } else if (registerKeyStatus === FAIL) {
          notifyMessages(registerKeyError ||
            intl.getI18nMessage('label.application.details.keys.register.key.error'), ALERT_ERROR);
          setRegisteringKey(false);
        }
        setShowLoading(false);
      }
    },[registerKeyStatus]);

    const generateSecret = (type) => {
      const { apiKey } = keyDetails;
      regenerateSecret({
        apiKey,
        appUuid: applicationUuid,
        keySecretHashed: type === 'HASHED_SECRET',
      });
      setShowLoading(true);
      setGeneratingSecret(true);
    };

    const getoauthPanelButton = () => {
      const { status, apiKey } = keyDetails;
      if (status === API_KEY_STATUS_DELETE_FAILED && isAdmin) {
        return (<Tooltip
            arrow disableFocusListener disableTouchListener placement="bottom"
            title={getI18nFormattedMessage('label.application.key.force.delete.confirmation.text')}
            ><Button
            onClick={deleteKeyOrAuthClient}
            className={classes.forceDeleteButton}
            disabled={locked
              || isEditDisabled
              || editKeyDisabledStates.includes(applicationDetails?.status)}
          >
            {getI18nFormattedMessage(isPortalAdmin? 'label.application.details.keys.overview.force.delete.button': 'label.delete.button')}
          </Button>
          </Tooltip>);
      }
      return (canEditOrDeleteKey ? <Button
        variant="outlined"
        onClick={() => {
          setIsEditKeyOpen(true);
        }}
        className={classes.editKeyButton}
        data-apim-test={`${apiKey}-edit-key-button`}
        disabled={locked || disabledByInternal
            || isEditDisabled || status === API_KEY_STATUS_DELETE_FAILED
            || editKeyDisabledStates.includes(applicationDetails?.status)}
      >
        {getI18nFormattedMessage('label.application.details.keys.overview.editkey.button')}
      </Button> : <span />);
    };

    const renderSecretDialog = () => {
      const keySecretHashed = get(regeneratedSecretDetails, 'keySecretHashed');
      return (
        <Dialog
          open={isSecretInfoDialogOpen}
          onClose={() => {
            setIsSecretInfoDialogOpen(false)
          }}
          aria-labelledby="generate-secret-dialog"
          id="generate-secret-dialog"
          data-apim-test={"generate-secret-dialog"}
          classes={{
            paper: classes.secretDialogPaperClass,
          }}
        >
          <div className={classes.generateSecretDialog}>
            <h3>{intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.heading')}</h3>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-key-${keyClientSecret}`}
                name={intl.getI18nMessage('label.application.key.shared.secret')}
                value={keyClientSecret}
                fieldContainerClass={classes.dialogTextField}
                disabled
                errorHelperText={false}
                helperText={''}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyClientSecret);
                  showCopied(keyClientSecret, 'SECRET', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>
            {keySecretHashed &&
              <div className={classes.secretHelpText}>
                <WarningIcon className={classes.secretInfoWarningIcon}/>
                <Typography>
                  {intl.getI18nFormattedMessage('label.application.key.shared.secret.hashed.generated.help')}
                </Typography>
              </div>}
            <div className={classes.dialogButtonPanel}>
              <Button
                color="primary"
                data-apim-test={"generate-secret-dialog-close-btn"}
                variant="outlined"
                onClick={() => {
                  onSaveDone();
                  setIsSecretInfoDialogOpen(false);
                }}
                className={classes.cancelButton}
              >
              {intl.getI18nFormattedMessage('label.close.button')}
              </Button>
            </div>
          </div>
        </Dialog>);
    };

    const renderRegisteredKeyInfoDialog = () => {
      return (
        <RegisterClientInfoDialog
          registeredClientId = {registeredClientId}
          keyClientSecret = {keyClientSecret}
          clientMetadata = {clientMetadata}
          notifyMessages = {notifyMessages}
          isRegisteredKeyInfoDialogOpen = {isRegisteredKeyInfoDialogOpen}
          setIsRegisteredKeyInfoDialogOpen = {setIsRegisteredKeyInfoDialogOpen}
          onRegistrationDialogClose = {onRegistrationDialogClose}
      />);
    };

  const getExpiryDateText = () => {
    let expiryTs = 0;
    if (isApiKeyExpiryEnabled && get(keyDetails, 'secretExpiryTs')) {
      expiryTs = get(keyDetails, 'secretExpiryTs');
    }
    if(expiryTs !== 0) {
        const keyExpiryDate = moment(expiryTs);
        return keyExpiryDate.format(
          'dddd, MMMM Do YYYY, HH:mm:ss',
        );
    }
    return getI18nFormattedMessage('label.application.details.apikeys.key.expiry.none');
  };
  const getExpiryDateDiff = () => {
    let expiryTs = 0;
    if (isApiKeyExpiryEnabled && get(keyDetails, 'secretExpiryTs')) {
      expiryTs = get(keyDetails, 'secretExpiryTs');
    }
    if(expiryTs !== 0) {
        const keyExpiryDate = moment(expiryTs);
        const currentTime = moment();
        return keyExpiryDate.diff(currentTime, 'days');
    }
    return 0;
  }
  const getExpiryDateSubText = () => {
    const keyStatus = keyDetails.status;
    const dateDiff = getExpiryDateDiff();
    if (keyStatus === 'EXPIRED' || dateDiff < 0) {
        return getI18nFormattedMessage('label.application.details.apikeys.expired');
    } else {
        return getI18nFormattedMessage('label.application.details.apikeys.expires', { days: dateDiff });
    }
  };

  const getExpiryDateSubTextClass = () => {
    let expiryDateSubTextClass = '';
    const keyStatus = keyDetails.status;
    if (keyStatus === 'EXPIRED' || getExpiryDateDiff() < 0) {
        expiryDateSubTextClass = classes.expiredKeyStatus;
    }
    return expiryDateSubTextClass;
  };

  const { apiKey, authProviderUuid } = keyDetails;

  const authProvider = authProviderUuid
    && find(authProviders, item => item.uuid === authProviderUuid);
  const authProviderName = authProvider && authProvider.name;
  const disableSecretGeneration = locked || isEditDisabled ||
    [API_KEY_STATUS_DELETE_FAILED, API_KEY_STATUS_DISABLED].includes(get(keyDetails, 'status'));

    return (
      <>
        {showLoading && <Loading />}
        <Grid
          container
          className={classes.singleKeyViewContainer}
          data-apim-test={`${apiKey}-single-key-view`}
        >
          {renderSecretDialog()}
          {renderRegisteredKeyInfoDialog()}
          <LoadingDialog
            className={classes.genericLoadingDialog}
            isOpen={showGenericLoadingDialog}
            title={intl.getI18nMessage('label.api.key.generic.loading.title')}
            description={intl.getI18nMessage('label.api.key.generic.loading.text')}
            dialogId="generic-loading-dialog"
          />
          <Grid item xs={6} className={classes.keyPanel}>
            <h3>{getI18nFormattedMessage(`label.application.details.key.overview.clientid.${lowerCase(get(keyDetails, 'authMethod'))}.title`)}</h3>
            {get(keyDetails, 'status') === API_KEY_STATUS_PENDING_REGISTRATION ?
            <div className={classes.pendingApiKey}>
              <FormLabel variant='h4'>{intl.getI18nMessage('label.application.key')}</FormLabel>
              <p>{getI18nFormattedMessage('label.application.details.oauthclient.pending.key')}</p>
              {canAddKey &&
                <Button
                  id="register-key-button"
                  data-apim-test="register-key-button"
                  variant="contained"
                  color="secondary"
                  className={classes.registerKeyButton}
                  onClick={() => {
                    registerKey({
                        appUuid: applicationUuid,
                        apiKey,
                      });
                      setShowLoading(true);
                      setRegisteringKey(true);
                  }}
                  disabled={locked || disabledByInternal || isEditDisabled ||
                    registerKeyDisabledStates.includes(applicationDetails?.status)}
                >
                  {getI18nFormattedMessage('label.application.details.keys.overview.register.key.button')}
                </Button>
              }
            </div> :
            (<div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-key-${keyDetails.apiKey}`}
                name={intl.getI18nMessage('label.application.key')}
                value={get(keyDetails, 'apiKey')}
                disabled
                fieldContainerClass={classes.textField}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyDetails.apiKey);
                  showCopied(keyDetails.apiKey, 'API_KEY', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>)}
            {get(keyDetails, 'authMethod') === 'SECRET' &&
            <>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-password-${keyDetails.keySecret}`}
                name={intl.getI18nMessage('label.application.key.shared.secret')}
                value={get(keyDetails, 'keySecret')}
                fieldContainerClass={classes.textField}
                disabled
              />
              {!keyDetails.keySecretHashed &&
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyDetails.keySecret);
                  showCopied(keyDetails.keySecret, 'SECRET', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>}
            </div>
            <Fragment>
              <Grid container className={classes.gridContainer}>
                <Grid md={12} item>
                  <Fragment>
                    <Grid container>
                      <Grid xs={12} sm={10} item className={classes.generateSecretContainer}>
                        <Tooltip
                            title={getI18nFormattedMessage(`label.application.key.shared.secret.generate.new.tool.tip${isLocking ? '.wf' : ''}`)}
                          arrow placement="top"
                        >
                            <Button
                              color="primary"
                              variant="outlined"
                              className={classes.generateSecretMenuButton}
                              aria-controls={'basic-menu'}
                              aria-haspopup="true"
                              aria-expanded="true"
                              id={`btn-key-generate-secret-${idx}`}
                              data-apim-test={`btn-key-generate-secret-${apiKey}`}
                              onClick={handleGenerateSecretClick}
                              disabled={disableSecretGeneration}
                            >
                              {getI18nFormattedMessage('label.application.details.keys.generatenew')}
                              {displayHashingOptions &&
                                <KeyboardArrowDown />
                              }
                            </Button>
                        </Tooltip>
                        {displayHashingOptions &&
                          <Menu
                            id="secret-type-menu"
                            anchorEl={anchorEl}
                            open={anchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            onClose={handleMenuClose}
                            MenuListProps={{
                              'aria-labelledby': 'basic-button',
                            }}
                            disabled={disableSecretGeneration}
                          >
                            <MenuItem onClick={() => handleGenerateSecretByType('PLAIN_SECRET')}>
                              {getI18nFormattedMessage('label.application.details.keys.generatenew.plain')}
                            </MenuItem>
                            <MenuItem onClick={() => handleGenerateSecretByType('HASHED_SECRET')}>
                              {getI18nFormattedMessage('label.application.details.keys.generatenew.hashed')}
                            </MenuItem>
                          </Menu>
                        }
                      {(isEditDisabled || locked) &&
                        <Tooltip
                          title={getI18nFormattedMessage(disabledByInternal? 'label.application.details.locked.by.internal.tooltip':
                          'label.application.details.keys.locked.tooltip')}
                          arrow placement="right">
                          <Lock className={classes.lockIcon2}/>
                        </Tooltip>
                      }
                      {!(isEditDisabled || locked) && isLocking &&
                        <Tooltip
                          title={getI18nFormattedMessage('label.application.details.keys.locking.tooltip')}
                          arrow placement="right">
                          <ReportProblem className={classes.warningIcon2}/>
                        </Tooltip>
                      }
                      </Grid>
                    </Grid>
                  </Fragment>
                </Grid>
              </Grid>
            </Fragment>
            </>}
            {isApiKeyExpiryEnabled && get(keyDetails, 'authMethod') === 'SECRET' && <div className={classes.expiryInfoSection}>
                <h3 data-apim-test="expiry-title">
                  {getI18nFormattedMessage('label.application.details.key.expiry.date.title')}
                </h3>
                <div data-apim-test="expiry-label">
                  <Typography variant="body1" className={classes.expiryDateText}>
                    {getExpiryDateSubText()}
                  </Typography>
                </div>
                <div data-apim-test="expiry-sub-label">
                  <Typography variant="body1" className={getExpiryDateSubTextClass()}>
                    {getExpiryDateText()}
                  </Typography>
              </div>
            </div>}
          </Grid>
          <Fragment>
            <Grid item xs={6} className={classes.oauthPanel}>
            {get(keyDetails, 'authMethod') === 'SECRET' &&
              (<>
                <h3>{getI18nFormattedMessage('label.application.details.key.overview.oauth.title')}</h3>
                {items.map((item) => displayItem(keyDetails, classes, item))}
                </>)
            }
            {get(keyDetails, 'authMethod') === 'NONE' &&
              (<><div>
              <h3>{getI18nFormattedMessage('label.application.details.keys.provider')}</h3>
              {authProviderName}
              </div>
              <div>
              <h3>{getI18nFormattedMessage('label.application.details.key.overview.auth.method')}</h3>
              {getI18nFormattedMessage('label.application.details.key.overview.auth.method.not.available')}
              </div></>)
            }
            {get(keyDetails, 'authMethod') === 'CERTIFICATE' &&
              (<div className={classes.certsTable}>
              <h3>{getI18nFormattedMessage('label.application.details.key.overview.certificates.title')}</h3>
              <SimpleTable
                rows={certificateList}
                columns={getCertListColumns()}
              />
              </div>)
            }
              <div className={classes.buttonContainer}>
                {getoauthPanelButton()}
                {(locked || isEditDisabled) &&
                  <Tooltip
                    title={getI18nFormattedMessage(disabledByInternal? 'label.application.details.locked.by.internal.tooltip':
                    'label.application.details.keys.locked.tooltip')}
                    arrow placement="right">
                    <Lock className={classes.lockIcon}/>
                  </Tooltip>
                }
                {!(locked || isEditDisabled) && isLocking && canEditOrDeleteKey &&
                  <Tooltip
                    title={getI18nFormattedMessage('label.application.details.keys.locking.tooltip')}
                    arrow placement="right">
                    <ReportProblem className={classes.warningIcon}/>
                  </Tooltip>
                }
              </div>
            </Grid>
          </Fragment>
        </Grid>
      </>
    );
};

SingleKeyView.propTypes = {
  applicationDetails: object,
  keyDetails: object,
  classes: object,
  notifyMessages: func,
  isAdmin: bool,
  isApiKeyExpiryEnabled: bool,
  setIsEditKeyOpen: func,
  isLocking: bool,
  locked: bool,
  disabledByInternal: bool,
  appSecretHashingMetadata: object,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  registeredKeyDetails: object,
  registerKeyStatus: string,
  registerKeyError: string,
  deleteKey: func,
  deleteOAuthClient: func,
  regenerateSecret: func,
  registerKey: func,
  onRegistrationDialogClose: func,
  onSaveDone: func,
  applicationUuid: string,
  idx: number,
  defaultHashType: string,
  isEditDisabled: bool,
  isPortalAdmin: bool,
  certificateList: arrayOf(object),
  earliestExpiryCertTs: number,
  urgentReportingDays: number,
  startReportingDays: number,
  authProviders: arrayOf(object),
  user: object,
  canAddKey: bool,
};

export default SingleKeyView;